// src/PublicBioPageDesktop.js
import React, { useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from 'react-query';
import { Helmet } from 'react-helmet';
import {
  Backdrop,
  Box,
  CircularProgress,
  CssBaseline,
  Divider,
  Grid,
  IconButton,
  Paper,
  Toolbar,
  Tooltip,
  Typography,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  Container,
  Fade,
  Zoom,
  Slide,
  Chip,
} from '@mui/material';
import { ThemeProvider, alpha } from '@mui/material/styles';
import { styled, keyframes } from '@mui/material/styles';
import QrCodeIcon from '@mui/icons-material/QrCode';
import QRCode from 'react-qr-code';
import CloseIcon from '@mui/icons-material/Close';
import GetAppIcon from '@mui/icons-material/GetApp';
import StarIcon from '@mui/icons-material/Star';
import BusinessIcon from '@mui/icons-material/Business';
import LinkIcon from '@mui/icons-material/Link';
import AnimationIcon from '@mui/icons-material/Animation';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewQuiltIcon from '@mui/icons-material/ViewQuilt';

import TikTokIcon from '../Components/Icons/TikTok';
import TwitchIcon from '../Components/Icons/Twitch';
import CastIcon from '@mui/icons-material/Cast';
import PodcastsIcon from '@mui/icons-material/Podcasts';
import TwitterIcon from '@mui/icons-material/Twitter';
import InstagramIcon from '@mui/icons-material/Instagram';
import YouTubeIcon from '@mui/icons-material/YouTube';

import API from '../API';
import HomeHeader from '../Components/HomeHeader';
import { lightTheme } from '../Utils/baseTheme';
import defaultPfp from '../Components/globalAssets/ppfLogo.png';

/*********************************************************************
 * Enhanced Animations with Backend Control
 *********************************************************************/
const shimmer = keyframes`
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
`;

const float = keyframes`
  0%, 100% { transform: translateY(0px); }
  50% { transform: translateY(-10px); }
`;

const pulse = keyframes`
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.05); }
`;

const gradientShift = keyframes`
  0% { background-position: 0% 50%; }
  50% { background-position: 100% 50%; }
  100% { background-position: 0% 50%; }
`;

const fadeInAnimation = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(20px); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0); 
  }
`;

const slideUpAnimation = keyframes`
  from { 
    opacity: 0; 
    transform: translateY(40px) scale(0.95); 
  }
  to { 
    opacity: 1; 
    transform: translateY(0) scale(1); 
  }
`;

// Animation wrapper component
const AnimatedWrapper = styled('div')(({ theme, animationType, animationsEnabled, delay = 0 }) => {
  if (!animationsEnabled) return {};
  
  const animationMap = {
    'FADE_IN': `${fadeInAnimation} 0.8s ease-out ${delay}ms both`,
    'SLIDE_UP': `${slideUpAnimation} 0.6s ease-out ${delay}ms both`,
  };

  return {
    animation: animationMap[animationType] || animationMap['FADE_IN'],
  };
});

/*********************************************************************
 * Enhanced Styled Components
 *********************************************************************/
const ModernCTAButton = styled(Button)(({ theme, variant = 'primary' }) => {
  const variants = {
    primary: {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      color: '#fff',
    },
    secondary: {
      background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
      color: '#fff',
    },
    success: {
      background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
      color: '#fff',
    }
  };

  return {
    ...variants[variant],
    borderRadius: '50px',
    padding: '12px 32px',
    fontWeight: 700,
    fontSize: '1rem',
    textTransform: 'none',
    boxShadow: '0 8px 32px rgba(102, 126, 234, 0.3)',
    border: 'none',
    position: 'relative',
    overflow: 'hidden',
    transition: 'all 0.3s ease',
    '&::before': {
      content: '""',
      position: 'absolute',
      top: 0,
      left: '-100%',
      width: '100%',
      height: '100%',
      background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
      transition: 'left 0.5s ease',
    },
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 40px rgba(102, 126, 234, 0.4)',
      '&::before': {
        left: '100%',
      },
    },
    '&:active': {
      transform: 'translateY(0px)',
    },
  };
});

const GlassButton = styled(Button)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: '16px',
  padding: '10px 24px',
  color: '#fff',
  fontWeight: 600,
  textTransform: 'none',
  transition: 'all 0.3s ease',
  '&:hover': {
    background: 'rgba(255, 255, 255, 0.2)',
    transform: 'translateY(-1px)',
    boxShadow: '0 8px 25px rgba(0, 0, 0, 0.2)',
  },
}));

const ModernHeroSection = styled('div')(() => ({
  position: 'relative',
  width: '100%',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  overflow: 'hidden',
}));

const HeroBackground = styled('div')(({ imageUrl }) => ({
  position: 'absolute',
  inset: 0,
  backgroundImage: `url(${imageUrl})`,
  backgroundSize: 'cover',
  backgroundPosition: 'center',
  backgroundAttachment: 'fixed',
  zIndex: 0,
  '&::after': {
    content: '""',
    position: 'absolute',
    inset: 0,
    background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)',
    backdropFilter: 'blur(1px)',
  },
}));

const HeroVideo = styled('video')(() => ({
  position: 'absolute',
  top: '50%',
  left: '50%',
  minWidth: '100%',
  minHeight: '100%',
  width: 'auto',
  height: 'auto',
  transform: 'translate(-50%, -50%)',
  objectFit: 'cover',
  zIndex: 0,
}));

const HeroContent = styled(Container)(({ theme }) => ({
  position: 'relative',
  zIndex: 2,
  textAlign: 'center',
  color: '#fff',
  padding: theme.spacing(4),
  maxWidth: '900px',
}));

const ModernAvatar = styled('img')(({ theme, animationsEnabled }) => ({
  width: 140,
  height: 140,
  borderRadius: '50%',
  objectFit: 'cover',
  border: '4px solid rgba(255, 255, 255, 0.2)',
  backdropFilter: 'blur(10px)',
  marginBottom: theme.spacing(3),
  animation: animationsEnabled ? `${float} 6s ease-in-out infinite` : 'none',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.3)',
}));

const SocialIconsContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexWrap: 'wrap',
  justifyContent: 'center',
  gap: theme.spacing(1.5),
  margin: theme.spacing(3, 0),
}));

const ModernSocialIcon = styled(IconButton)(({ theme }) => ({
  width: 56,
  height: 56,
  background: 'rgba(255, 255, 255, 0.1)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  color: '#fff',
  borderRadius: '16px',
  transition: 'all 0.3s ease',
  '&:hover': {
    transform: 'translateY(-3px) scale(1.1)',
    background: 'rgba(255, 255, 255, 0.2)',
    boxShadow: '0 10px 30px rgba(0, 0, 0, 0.3)',
  },
}));

const ContentSection = styled(Box)(({ theme }) => ({
  padding: theme.spacing(6, 0),
  background: 'linear-gradient(180deg, #f8fafc 0%, #e2e8f0 100%)',
  minHeight: '50vh',
}));

const GlassCard = styled(Paper)(({ theme, animationsEnabled }) => ({
  background: 'rgba(255, 255, 255, 0.7)',
  backdropFilter: 'blur(20px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  borderRadius: '24px',
  padding: theme.spacing(4),
  margin: theme.spacing(0, 2),
  boxShadow: '0 25px 50px rgba(0, 0, 0, 0.1)',
  overflow: 'hidden',
  position: 'relative',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #667eea, transparent)',
    animation: animationsEnabled ? `${shimmer} 3s ease-in-out infinite` : 'none',
  },
}));

const ModernItemCard = styled(Box)(({ theme, animationsEnabled }) => ({
  position: 'relative',
  borderRadius: '20px',
  overflow: 'hidden',
  cursor: 'pointer',
  transition: 'all 0.3s ease',
  background: '#fff',
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
  '&:hover': {
    transform: animationsEnabled ? 'translateY(-5px) scale(1.02)' : 'translateY(-3px)',
    boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
    '& .item-overlay': {
      opacity: 1,
    },
    '& .item-media': {
      transform: animationsEnabled ? 'scale(1.05)' : 'scale(1.02)',
    },
  },
}));

const ItemMediaContainer = styled('div')(() => ({
  width: '100%',
  height: '200px',
  position: 'relative',
  overflow: 'hidden',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
}));

const ItemMedia = styled('div')(() => ({
  width: '100%',
  height: '100%',
  transition: 'transform 0.3s ease',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}));

const ModernOverlay = styled('div')(({ theme }) => ({
  position: 'absolute',
  inset: 0,
  background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.9) 0%, rgba(118, 75, 162, 0.9) 100%)',
  backdropFilter: 'blur(10px)',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  opacity: 0,
  transition: 'opacity 0.3s ease',
  textAlign: 'center',
  padding: theme.spacing(2),
  color: '#fff',
}));

const ModernDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiDialog-paper': {
    borderRadius: '24px',
    background: 'rgba(255, 255, 255, 0.95)',
    backdropFilter: 'blur(20px)',
    border: '1px solid rgba(255, 255, 255, 0.3)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.2)',
  },
}));

const QRContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  padding: theme.spacing(3),
  background: '#fff',
  borderRadius: '16px',
  margin: theme.spacing(2),
  boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)',
}));

const HeroTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 800,
  fontSize: 'clamp(2rem, 8vw, 4rem)',
  background: 'linear-gradient(135deg, #fff 0%, rgba(255,255,255,0.8) 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  marginBottom: theme.spacing(2),
  lineHeight: 1.2,
}));

const HeroSubtitle = styled(Typography)(({ theme }) => ({
  fontSize: 'clamp(1rem, 4vw, 1.5rem)',
  color: 'rgba(255, 255, 255, 0.9)',
  fontWeight: 400,
  lineHeight: 1.6,
  maxWidth: '600px',
  margin: '0 auto',
}));

const PageTypeIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 20,
  right: 20,
  zIndex: 3,
}));

const AnimationIndicator = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 20,
  left: 20,
  zIndex: 3,
  display: 'flex',
  gap: theme.spacing(1),
}));

/******************** vCard builder ************************/
function buildVCard(contact) {
  let lines = [
    'BEGIN:VCARD',
    'VERSION:3.0',
    `FN:${contact.full_name}`
  ];
  if (contact.first_name || contact.last_name) {
    lines.push(
      `N:${contact.last_name || ''};${contact.first_name || ''};${contact.middle_name || ''};${contact.nickname || ''}`
    );
  }
  if (contact.email) lines.push(`EMAIL;TYPE=INTERNET:${contact.email}`);
  if (contact.phone) lines.push(`TEL;TYPE=CELL:${contact.phone}`);
  if (contact.company) lines.push(`ORG:${contact.company}`);
  if (contact.title) lines.push(`TITLE:${contact.title}`);
  lines.push('END:VCARD');
  return lines.join('\n');
}

export default function PublicBioPageDesktop() {
  const { slug: rawSlug } = useParams();
  const [qrOpen, setQrOpen] = useState(false);
  const slug = decodeURIComponent(rawSlug);
  const cleanSlug = slug.startsWith('@') ? slug.slice(1) : slug;
  /* ─────────────────── Fetch bio page first ─────────────────── */
  const { data: bioPage, isLoading: bioLoading, error: bioErr } = useQuery(
    ['bioPage', cleanSlug],
    () => API.bio.getPublic(cleanSlug),
    { enabled: !!cleanSlug, refetchOnWindowFocus: false }
  );
  
  // Only fetch creator data if the bio page belongs to a creator (has username field)
  const shouldFetchCreator = bioPage?.username;
  
  const { data: creatorResp, isLoading: creatorLoading, error: creErr } = useQuery(
    ['creatorInfo', bioPage?.username],
    () => API.creators.fetchDetails(bioPage.username),
    { 
      enabled: !!shouldFetchCreator, 
      refetchOnWindowFocus: false 
    }
  );

  const loading = bioLoading || (shouldFetchCreator && creatorLoading);
  const error = bioErr || (shouldFetchCreator && creErr);

  if (loading) {
    return (
      <Backdrop open sx={{ zIndex: 9999, background: 'rgba(0,0,0,0.8)' }}>
        <Box display="flex" flexDirection="column" alignItems="center" gap={2}>
          <CircularProgress size={60} sx={{ color: '#667eea' }} />
          <Typography variant="h6" color="#fff">Loading...</Typography>
        </Box>
      </Backdrop>
    );
  }

  if (error) {
    return (
      <Box p={4} textAlign="center">
        <Typography variant="h4" color="error" gutterBottom>
          Oops! Something went wrong
        </Typography>
        <Typography color="text.secondary">
          {error.message || 'Error loading data.'}
        </Typography>
      </Box>
    );
  }

  if (!bioPage) {
    return (
      <Box p={4} textAlign="center" minHeight="100vh" display="flex" alignItems="center" justifyContent="center">
        <Box>
          <Typography variant="h4" gutterBottom>Page Not Found</Typography>
          <Typography color="text.secondary">This page does not exist.</Typography>
        </Box>
      </Box>
    );
  }

  /* ───────────── Extract enhanced page info ───────────── */
  const { 
    title, 
    description, 
    header_media_url, 
    items = [], 
    qr_enabled, 
    self_contact,
    page_type,
    view_type,
    animations_enabled = true,
    animation_type = 'FADE_IN',
    link_user_page,
    linked_page_url
  } = bioPage;
  
  // Handle both creator and user bio pages
  let displayInfo = {
    display_name: title || slug,
    pfphref: defaultPfp,
    tiktok_link: null,
    instagram_link: null,
    youtube_link: null,
    twitter_link: null,
    twitch_link: null,
    snap_link: null,
    x_link: null,
    kick_link: null,
    podcast_link: null,
    creator: null
  };

  // If this is a creator's bio page and we have creator data
  if (creatorResp?.creator) {
    const creator = creatorResp.creator;
    displayInfo = {
      display_name: title || creator.display_name || creator.creator,
      pfphref: creator.pfphref || defaultPfp,
      tiktok_link: creator.tiktok_link,
      instagram_link: creator.instagram_link,
      youtube_link: creator.youtube_link,
      twitter_link: creator.twitter_link,
      twitch_link: creator.twitch_link,
      snap_link: creator.snap_link,
      x_link: creator.x_link,
      kick_link: creator.kick_link,
      podcast_link: creator.podcast_link,
      creator: creator.creator
    };
  }

  const isHeaderImage = header_media_url && /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(header_media_url);
  const sorted = [...items].sort((a, b) => (a.order_index ?? 0) - (b.order_index ?? 0));

  /* ───────────── Animation Helper ───────────── */
  const getAnimationDelay = (index) => animations_enabled ? index * 150 : 0;

  /* ───────────── Enhanced Render Helper ───────────── */
  const renderItem = (item, index) => {
    const getGridSize = (size, viewType) => {
      if (viewType === 'complex') {
        // Complex view uses more varied sizing
        switch (size) {
          case 'LARGE': return { xs: 12, sm: 12, md: 8, lg: 6 };
          case 'MEDIUM': return { xs: 12, sm: 6, md: 6, lg: 4 };
          case 'SMALL': return { xs: 12, sm: 6, md: 4, lg: 3 };
          default: return { xs: 12, sm: 6, md: 4, lg: 3 };
        }
      } else {
        // Simple view uses consistent sizing
        switch (size) {
          case 'LARGE': return { xs: 12, sm: 12, md: 12 };
          case 'MEDIUM': return { xs: 12, sm: 6, md: 6 };
          case 'SMALL': return { xs: 12, sm: 6, md: 4 };
          default: return { xs: 12, sm: 6, md: 4 };
        }
      }
    };

    const AnimationComponent = animations_enabled ? 
      (animation_type === 'SLIDE_UP' ? Slide : Fade) : 
      Box;

    const animationProps = animations_enabled ? {
      in: true,
      timeout: 600 + getAnimationDelay(index),
      ...(animation_type === 'SLIDE_UP' && { direction: "up" })
    } : {};

    if (item.component_type === 'VIDEO_EMBED' && item.url) {
      return (
        <Grid item {...getGridSize(item.size, view_type)} key={item.id}>
          <AnimatedWrapper
            animationType={animation_type}
            animationsEnabled={animations_enabled}
            delay={getAnimationDelay(index)}
          >
            <ModernItemCard 
              onClick={() => window.open(item.url, '_blank')}
              animationsEnabled={animations_enabled}
            >
              <Box sx={{ position: 'relative', width: '100%', paddingBottom: '56.25%' }}>
                <iframe
                  title={item.label || 'video'}
                  src={item.url}
                  width="100%"
                  height="100%"
                  style={{
                    border: 'none',
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    borderRadius: '20px 20px 0 0'
                  }}
                  allowFullScreen
                />
              </Box>
              <ModernOverlay className="item-overlay">
                {item.label && (
                  <Typography variant="h6" sx={{ fontWeight: 700 }}>
                    {item.label}
                  </Typography>
                )}
                {item.description && (
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                    {item.description}
                  </Typography>
                )}
              </ModernOverlay>
            </ModernItemCard>
          </AnimatedWrapper>
        </Grid>
      );
    }

    return (
      <Grid item {...getGridSize(item.size, view_type)} key={item.id}>
        <AnimatedWrapper
          animationType={animation_type}
          animationsEnabled={animations_enabled}
          delay={getAnimationDelay(index)}
        >
          <ModernItemCard 
            onClick={() => item.url && window.open(item.url, '_blank')}
            animationsEnabled={animations_enabled}
          >
            <ItemMediaContainer>
              <ItemMedia className="item-media">
                {item.media_url ? (
                  /\.(gif|jpe?g|tiff?|png|webp|bmp)$/i.test(item.media_url) ? (
                    <Box
                      component="img"
                      src={item.media_url}
                      alt={item.label || 'img'}
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  ) : (
                    <video
                      src={item.media_url}
                      autoPlay
                      loop
                      muted
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        display: 'block'
                      }}
                    />
                  )
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: 'text.secondary'
                    }}
                  >
                    <Typography variant="overline">No Media</Typography>
                  </Box>
                )}
              </ItemMedia>
            </ItemMediaContainer>
            <ModernOverlay className="item-overlay">
              {item.label && (
                <Typography variant="h6" sx={{ fontWeight: 700 }}>
                  {item.label}
                </Typography>
              )}
              {item.description && (
                <Typography variant="body2" sx={{ mt: 1, opacity: 0.9 }}>
                  {item.description}
                </Typography>
              )}
            </ModernOverlay>
          </ModernItemCard>
        </AnimatedWrapper>
      </Grid>
    );
  };

  /* ───────────── vCard + QR helpers ───────────── */
  const vcardStr = self_contact ? buildVCard(self_contact) : '';
  const vcardData = `data:text/vcard;charset=utf-8,${encodeURIComponent(vcardStr)}`;

  return (
    <>
      <Helmet>
        <title>{displayInfo.display_name}</title>
        <meta name="description" content={description || `Bio page for ${displayInfo.display_name}`} />
      </Helmet>
      <ThemeProvider theme={lightTheme}>
        <CssBaseline />
        <HomeHeader />
        <Toolbar />

        {/* ───────────── HERO SECTION ───────────── */}
        <ModernHeroSection>
          {/* Page Type Indicator */}        

          {header_media_url ? (
            isHeaderImage ? (
              <HeroBackground imageUrl={header_media_url} />
            ) : (
              <>
                <HeroVideo src={header_media_url} autoPlay muted loop />
                <Box
                  sx={{
                    position: 'absolute',
                    inset: 0,
                    background: 'linear-gradient(135deg, rgba(0,0,0,0.4) 0%, rgba(0,0,0,0.6) 100%)',
                    zIndex: 1
                  }}
                />
              </>
            )
          ) : (
            <Box
              sx={{
                position: 'absolute',
                inset: 0,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              }}
            />
          )}

          <HeroContent>
            <AnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={200}
            >
              <Box>
                {!isHeaderImage && (
                  <ModernAvatar 
                    src={displayInfo.pfphref} 
                    alt={displayInfo.display_name}
                    animationsEnabled={animations_enabled}
                  />
                )}
                <HeroTitle variant="h1">
                  {displayInfo.display_name}
                </HeroTitle>
                {description && (
                  <HeroSubtitle variant="h5">
                    {description}
                  </HeroSubtitle>
                )}
              </Box>
            </AnimatedWrapper>

            {/* Social Icons - only show for creators */}
            {creatorResp?.creator && (
              <AnimatedWrapper
                animationType={animation_type}
                animationsEnabled={animations_enabled}
                delay={400}
              >
                <SocialIconsContainer>
                  {displayInfo.tiktok_link && (
                    <Tooltip title="TikTok" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.tiktok_link, '_blank')}>
                        <TikTokIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.instagram_link && (
                    <Tooltip title="Instagram" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.instagram_link, '_blank')}>
                        <InstagramIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.youtube_link && (
                    <Tooltip title="YouTube" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.youtube_link, '_blank')}>
                        <YouTubeIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.twitter_link && (
                    <Tooltip title="Twitter" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.twitter_link, '_blank')}>
                        <TwitterIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.twitch_link && (
                    <Tooltip title="Twitch" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.twitch_link, '_blank')}>
                        <TwitchIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.snap_link && (
                    <Tooltip title="Snapchat" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.snap_link, '_blank')}>
                        <LinkIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.x_link && (
                    <Tooltip title="X" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.x_link, '_blank')}>
                        <TwitterIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.kick_link && (
                    <Tooltip title="Kick" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.kick_link, '_blank')}>
                        <CastIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                  {displayInfo.podcast_link && (
                    <Tooltip title="Podcast" placement="top">
                      <ModernSocialIcon onClick={() => window.open(displayInfo.podcast_link, '_blank')}>
                        <PodcastsIcon />
                      </ModernSocialIcon>
                    </Tooltip>
                  )}
                </SocialIconsContainer>
              </AnimatedWrapper>
            )}

            {/* CTA Buttons */}
            <AnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={600}
            >
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2, mt: 4 }}>
                {/* Show "Let's Chat" button for creators */}
                {displayInfo.creator && (
                  <ModernCTAButton
                    size="large"
                    variant="primary"
                    onClick={() => window.open(`/creators/${displayInfo.creator}`, '_self')}
                  >
                    Let's Chat
                  </ModernCTAButton>
                )}

                {/* Show linked page button for profile pages */}
                {page_type === 'PROFILE' && link_user_page && linked_page_url && (
                  <ModernCTAButton
                    size="large"
                    variant="secondary"
                    startIcon={<LinkIcon />}
                    onClick={() => window.open(linked_page_url, '_self')}
                  >
                    View My {bioPage.username ? 'Mediakit' : 'Profile'}
                  </ModernCTAButton>
                )}

                {qr_enabled && self_contact && (
                  <GlassButton
                    startIcon={<QrCodeIcon />}
                    onClick={() => setQrOpen(true)}
                  >
                    Save My Contact
                  </GlassButton>
                )}
              </Box>
            </AnimatedWrapper>
          </HeroContent>
        </ModernHeroSection>

        {/* ───────────── CONTENT SECTION ───────────── */}
        <ContentSection>
          <Container maxWidth="lg">
            <AnimatedWrapper
              animationType={animation_type}
              animationsEnabled={animations_enabled}
              delay={800}
            >
              <GlassCard elevation={0} animationsEnabled={animations_enabled}>
                <Typography
                  variant="h4"
                  gutterBottom
                  sx={{
                    fontWeight: 700,
                    textAlign: 'center',
                    mb: 4,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                  }}
                >
                  My Links & Content
                </Typography>
                
                <Divider sx={{ mb: 4, background: 'linear-gradient(90deg, transparent, #667eea, transparent)', height: 2, border: 'none' }} />
                
                <Grid container spacing={view_type === 'complex' ? 2 : 3}>
                  {sorted.map((item, index) => renderItem(item, index))}
                </Grid>

                {/* Enhanced Watermark */}
                <AnimatedWrapper
                  animationType={animation_type}
                  animationsEnabled={animations_enabled}
                  delay={1000 + sorted.length * 150}
                >
                  <Box sx={{ textAlign: 'center', mt: 6, opacity: 0.6 }}>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      Powered by Blitz ✨
                    </Typography>
                  </Box>
                </AnimatedWrapper>
              </GlassCard>
            </AnimatedWrapper>
          </Container>
        </ContentSection>

        {/* ───────────── QR CODE DIALOG ───────────── */}
        <ModernDialog
          open={qrOpen}
          onClose={() => setQrOpen(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>
                Save My Contact Info
              </Typography>
              <IconButton onClick={() => setQrOpen(false)} size="small">
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <QRContainer>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 3, textAlign: 'center' }}>
                Scan with your phone's camera to add me to your contacts
              </Typography>
              <QRCode
                value={vcardData}
                size={200}
                style={{
                  height: 'auto',
                  maxWidth: '100%',
                  width: '100%',
                  background: '#fff',
                  padding: '16px',
                  borderRadius: '12px',
                }}
                viewBox="0 0 256 256"
              />
              <Button
                variant="contained"
                startIcon={<GetAppIcon />}
                href={vcardData}
                download={`${self_contact?.full_name || 'contact'}.vcf`}
                sx={{
                  mt: 3,
                  borderRadius: '12px',
                  textTransform: 'none',
                  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                  fontWeight: 600,
                }}
              >
                Download vCard
              </Button>
            </QRContainer>
          </DialogContent>
        </ModernDialog>
      </ThemeProvider>
    </>
  );
}